





























































































import { Component, Vue } from "vue-property-decorator";
import { TaxObligationPaymentPayload } from "@/dto/tax/TaxObligationPaymentPayload";
import TaxObligationService from "@/services/TaxObligationService";
import PortalInput from "@/components/common/PortalInput.vue";
import PortalSelect from "@/components/common/PortalSelect.vue";
import { PaymentMethod, TransactionType } from "@/constants/PaymentConstants";
import SelectOption from "@/components/common/SelectOption";
import { processError } from "@/utils/ComponentUtils";

@Component({
  components: {
    PortalInput,
    PortalSelect
  }
})
export default class AddTaxPaymentModal extends Vue {
  private taxObligationId: number | null = null;
  private form: any = {
    paymentDate: new Date().toISOString().substr(0, 10),
    paymentMethod: null,
    paymentAccount: '',
    items: []
  };

  public beforeOpen(event: any): void {
    this.taxObligationId = event.params.taxObligationId;
    this.form = {
      paymentDate: new Date().toISOString().substr(0, 10),
      paymentMethod: PaymentMethod.CUSTOMER_CARD,
      paymentAccount: '',
      items: [
        {
          transactionType: null,
          amountCents: 0,
          amountDisplay: 0
        }
      ]
    };
    if (this.$validator) {
      this.$validator.reset();
    }
  }

  public closeModal(): void {
    this.$modal.hide('add-tax-payment-modal');
  }

  public addItem(): void {
    if (this.canAddItem) {
      this.form.items.push({
        transactionType: null,
        amountCents: 0,
        amountDisplay: 0
      });
    }
  }

  public removeItem(index: number): void {
    this.form.items.splice(index, 1);
  }

  get totalAmountCents(): number {
    return this.form.items.reduce((sum: number, item: any) => sum + (item.amountCents || 0), 0);
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  get canAddItem(): boolean {
    const selectedTypes = this.form.items.map((item: any) => item.transactionType);
    const availableTypes = [TransactionType.OBLIGATION_PAYMENT, TransactionType.PENALTY_PAYMENT];
    return this.form.items.length < availableTypes.length && availableTypes.some(type => !selectedTypes.includes(type));
  }

  get isValid(): boolean {
    if (this.$validator && this.$validator.errors.items.length > 0) return false;
    const basicValid = !!(this.form.paymentDate && this.form.paymentMethod && this.form.paymentAccount);
    const itemsValid = this.form.items.length > 0 && this.form.items.every((item: any) => !!item.transactionType && (item.amountDisplay > 0 || item.amountCents > 0));
    return basicValid && itemsValid;
  }

  get paymentMethodOptions(): SelectOption[] {
    const allowedMethods = [
      PaymentMethod.CUSTOMER_CARD,
      PaymentMethod.SUBLIME_CARD,
      PaymentMethod.SUBLIME_ACCOUNT,
      PaymentMethod.CUSTOMER_BUSINESS_ACCOUNT,
      PaymentMethod.CUSTOMER_PERSONAL_ACCOUNT,
    ];
    return allowedMethods.map(method => 
      SelectOption.builder().title(this.$t('tax_calendar.payment_methods.' + method) as string).value(method).build()
    );
  }

  get isCardPayment(): boolean {
    return [PaymentMethod.CUSTOMER_CARD, PaymentMethod.SUBLIME_CARD].includes(this.form.paymentMethod);
  }

  get paymentAccountLabel(): string {
    return this.isCardPayment ? this.$t('tax_calendar.payment_account.card_label') as string : this.$t('tax_calendar.payment_account.account_label') as string;
  }

  get paymentAccountPlaceholder(): string {
    return this.isCardPayment ? this.$t('tax_calendar.payment_account.card_placeholder') as string : this.$t('tax_calendar.payment_account.account_placeholder') as string;
  }

  public getTransactionTypeOptions(currentType: TransactionType): SelectOption[] {
    const selectedTypes = this.form.items
      .map((item: any) => item.transactionType)
      .filter((type: any) => type && type !== currentType);

    const allowedTypes = [TransactionType.OBLIGATION_PAYMENT, TransactionType.PENALTY_PAYMENT];

    return allowedTypes
      .filter(type => !selectedTypes.includes(type))
      .map(type => 
        SelectOption.builder().title(this.$stringUtils.enumToFormattedSentence(type)).value(type).build()
      );
  }

  public processAmountInput(index: number): void {
    let val = String(this.form.items[index].amountDisplay);
    
    val = val.replace(',', '.');
    val = val.replace(/[^0-9.]/g, '');
    
    const parts = val.split('.');
    if (parts.length > 2) {
      val = parts[0] + '.' + parts.slice(1).join('');
    }
    
    if (val.includes('.')) {
      const [int, dec] = val.split('.');
      if (dec && dec.length > 2) {
        val = `${int}.${dec.slice(0, 2)}`;
      }
    }

    this.form.items[index].amountDisplay = val;
  }

  public async submit(): Promise<void> {
    const valid = await this.$validator.validateAll();
    if (!valid || !this.isValid || !this.taxObligationId) return;

    this.form.items.forEach((item: any) => {
      item.amountCents = Math.round(parseFloat(item.amountDisplay) * 100);
    });

    const payload: TaxObligationPaymentPayload = {
      paymentDate: this.form.paymentDate,
      paymentMethod: this.form.paymentMethod,
      paymentAccount: this.form.paymentAccount,
      totalAmountCents: this.totalAmountCents,
      items: this.form.items
    };

    this.$loadOn();
    try {
      await TaxObligationService.addPayment(this.taxObligationId, payload);
      this.closeModal();
      this.$emit('added');
    } catch (e) {
      processError(e, this);
    } finally {
      this.$loadOff();
    }
  }
}
